import React, { useCallback, useEffect, useState } from "react";
import { Bug, Check, Loader2, RefreshCw } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const KIND = {
  render: "broke the page",
  window: "uncaught error",
  promise: "failed request",
};

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// Every break a visitor actually hit, newest first. You get one email the first time a break shows
// up (and once a day while it lasts), so this panel is the follow-up, not the alarm.
export const AdminCrashes = () => {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("open");
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState("");

  const load = useCallback(async () => {
    try { setD((await api.get(`/admin/client-errors?status=${status}`)).data); } catch { setD(null); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (fp) => {
    setBusy(fp);
    try {
      await api.post(`/admin/client-errors/${fp}/resolve`);
      toast({ title: "Marked fixed", description: "If it happens again you'll hear about it." });
      await load();
    } catch { toast({ title: "Could not update that" }); }
    finally { setBusy(""); }
  };

  if (!d) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-crashes">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold text-foreground flex items-center gap-2">
            <Bug size={18} className="text-[#16a34a]" /> Crashes
            {d.openCount > 0 && (
              <span className="text-[11px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 px-2 py-0.5 rounded-full" data-testid="admin-crashes-count">
                {d.openCount} open
              </span>
            )}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pages that broke in somebody's browser. You're emailed the first time each one appears.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-secondary rounded-full p-1">
            {["open", "all"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`admin-crashes-filter-${s}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold capitalize transition-all ${status === s ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>
                {s}
              </button>
            ))}
          </div>
          <button onClick={load} data-testid="admin-crashes-refresh"
            className="inline-flex items-center gap-2 border border-border hover:border-[#16a34a] text-foreground text-sm font-semibold px-3 py-2 rounded-xl transition-colors">
            <RefreshCw size={15} />
          </button>
        </div>
      </div>

      {d.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4 flex items-center gap-2" data-testid="admin-crashes-empty">
          <Check size={15} className="text-[#16a34a]" /> Nothing broke. Quiet is good.
        </p>
      ) : (
        <div className="mt-4 space-y-2">
          {d.rows.map((r) => (
            <div key={r.fp} className="border border-border rounded-xl p-3" data-testid={`admin-crash-row-${r.fp}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground break-words">
                    {r.message}
                    <span className="ml-2 text-[11px] font-bold bg-secondary text-muted-foreground px-2 py-0.5 rounded-full">
                      {r.count}× · {KIND[r.kind] || r.kind}
                    </span>
                    {r.resolvedAt && (
                      <span className="ml-2 text-[11px] font-bold bg-[#e7f6ec] text-[#0f7a3d] px-2 py-0.5 rounded-full">fixed</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {r.path || "unknown page"}{r.label ? ` · ${r.label}` : ""}{r.email ? ` · ${r.email}` : " · signed out"}
                  </p>
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                    first {when(r.firstSeen)} · last {when(r.lastSeen)}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setOpen(open === r.fp ? "" : r.fp)} data-testid={`admin-crash-stack-${r.fp}`}
                    className="text-xs font-semibold border border-border hover:border-[#16a34a] text-foreground px-3 py-2 rounded-xl transition-colors">
                    {open === r.fp ? "Hide detail" : "Detail"}
                  </button>
                  {!r.resolvedAt && (
                    <button onClick={() => resolve(r.fp)} disabled={!!busy} data-testid={`admin-crash-resolve-${r.fp}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white px-3 py-2 rounded-xl transition-colors">
                      {busy === r.fp ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Mark fixed
                    </button>
                  )}
                </div>
              </div>
              {open === r.fp && (
                <pre className="mt-3 text-[11px] leading-relaxed text-muted-foreground bg-secondary rounded-xl p-3 overflow-x-auto whitespace-pre-wrap" data-testid={`admin-crash-detail-${r.fp}`}>
                  {r.stack || "No stack captured."}
                  {r.ua ? `\n\n${r.ua}` : ""}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCrashes;
